"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Award, ShieldCheck, HardHat, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
export default function FleetIntro() {
  const shouldReduceMotion = useReducedMotion();
  const trustPoints = [{
    icon: ShieldCheck,
    title: "NASC accredited",
    description: "Audited safety standards and full compliance on every build."
  }, {
    icon: Award,
    title: "CHAS approved",
    description: "Rigorous health and safety pre-qualification across all work."
  }, {
    icon: HardHat,
    title: "20+ years experience",
    description: "Led by Pete Wood, delivering dependable access solutions."
  }];
  return <section id="fleetlocalintro" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="max-w-3xl">
          <Badge variant="outline" className="mb-4 px-3 py-1 text-xs font-semibold text-primary border-primary/30">
            Hampshire scaffolding contractors
          </Badge>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default mb-6 leading-tight">
            Scaffolding in Fleet
          </h2>

          <div className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed space-y-4">
            <p className="text-foreground font-medium">
              BMF Scaffolding provides commercial, residential, roofing, solar and specialist scaffolding in Fleet and the surrounding Hampshire area, including GU51 and GU52.
            </p>
            <p>
              We adapt our access solutions to Fleet’s distinct layout, from busy town-centre shopfronts along Fleet Road that require careful pedestrian protection, to quiet residential cul-de-sacs and larger semi-rural properties across Church Crookham, Elvetham Heath, and surrounding villages.
            </p>
            <p>
              Led by Pete Wood with more than 20 years of hands-on scaffolding experience, our team approaches every structure with a strong focus on safety and site tidiness. We operate with full NASC and CHAS accreditation, ensuring domestic renovations, roofing projects, and commercial maintenance stay secure and on schedule.
            </p>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactRequestQuotePage">
                Request a quote
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-border hover:bg-accent hover:text-accent-foreground">
              <Link to="/AreasWeCoverPage">
                View coverage area
              </Link>
            </Button>
          </div>
        </motion.div>

        <div className="mt-12 pt-10 border-t border-border grid grid-cols-1 sm:grid-cols-3 gap-6">
          {trustPoints.map((item, i) => {
          const Icon = item.icon;
          return <motion.div key={item.title} data-index={i} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.4,
            delay: i * 0.1,
            ease: "easeOut"
          }}>
                <Card className="h-full border border-border bg-card/60 shadow-xs hover:border-primary/40 transition-[border-color,box-shadow]">
                  <CardContent className="p-5 flex flex-col justify-between h-full">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 rounded-md bg-muted text-primary">
                        <Icon className="size-5" />
                      </div>
                      <h2 className="font-semibold text-foreground text-sm font-default">
                        {item.title}
                      </h2>
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground font-default">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}